/* =====================================================================
   claude-scroll-lock-shim.js
   ---------------------------------------------------------------------
   FIX: Chrome-Android-only upward shift of position:fixed overlays
        (the ReCharge shadow-DOM cart drawer + the theme's own bars).

   SYMPTOM (real mobile Chrome only; mobile Safari is fine; desktop &
   emulated Chromium do NOT reproduce — they have a static viewport)
   When the cart opens as a full-screen overlay AFTER the page has been
   scrolled and the URL bar has collapsed, the whole overlay is shifted
   UP by ~the URL-bar height: its "Your cart" header + close (X) clip
   above the visible top, and page content shows through a gap at the
   bottom. The same upward shift hits any position:fixed/sticky element.

   MECHANISM (the defensible version — see notes below)
   On Chrome-Android the LAYOUT viewport (the Initial Containing Block
   that position:fixed resolves against) and the VISUAL viewport (what
   the user sees) DIVERGE while the URL bar animates, and only RECONCILE
   once scrolling settles. ReCharge's scroll-lock is NON-PINNING: it sets
   body{overflow:hidden} (stylesheet rule) + inline pointer-events:none,
   but it never pins scroll (no body{position:fixed; top:-scrollY}). When
   that overflow:hidden lands mid-scroll, the page is frozen in the
   UNRECONCILED state — so the overlay's inset:0 paints against a layout
   viewport whose top sits above the visible top. Mobile Safari keeps
   fixed glued to the VISUAL viewport, so it never shows the gap.

   THE FIX (document-level — no shadow-DOM access needed)
   When ANY full-screen lock is detected, replace the weak lock with the
   canonical PINNING lock the theme ALREADY uses for its own dialogs
   (assets/dialog.js:56-58/94-97): capture scrollY, then
   body{position:fixed; top:-scrollY; left:0; right:0; width:100%}.
   Pinning the document at a fixed, scroll-compensated offset forces the
   scroll origin to a stable 0, which makes Chrome RECONCILE the layout
   and visual viewports — so every position:fixed element (the ReCharge
   shadow-DOM overlay AND the theme's own bars) re-anchors to the visible
   viewport. We never reach into the shadow DOM; we only change the HOST
   document's scroll/viewport context, which is the overlay's containing
   block. On unlock, restore styles and window.scrollTo(0, savedY).

   NOTE — why this is NOT "fixed reads scrollY" cargo-culting:
   position:fixed is detached from scroll; the cure is not "move the
   origin by scrollY". It is that forcing a stable 0 scroll origin (via
   the body pin) removes the unreconciled layout/visual divergence the
   collapsing URL bar created. This is the same lever dialog.js already
   ships and which already works on Chrome-Android for native modals.

   IF RESIDUAL SHIFT REMAINS after on-device testing (Step 2 fallback):
   the next lever is sizing the locked surface to the visual viewport —
   listen to window.visualViewport 'resize'/'scroll' and set the locked
   body height to window.visualViewport.height. That targets an overlay-
   HEIGHT mismatch (the one thing a document pin cannot reach, because it
   is computed inside the shadow tree). Stub left at the bottom, OFF by
   default. Do not enable without a real-device repro of residual shift.

   COEXISTENCE
   - assets/dialog.js pins body{position:fixed; top:-scrollY} for native
     Horizon modals (cart-drawer / quick-add / predictive-search all go
     through it). It NEVER sets body{overflow:hidden} or pointer-events,
     so our detector does not fire for those. As a hard guarantee we ALSO
     bail if body is already position:fixed (dialog.js owns the pin then).
   - sections/custom-free-tee-bar.liquid runs an identical cftbIsLocked()
     observer that display:none's the free-tee bar during a lock. That is
     harmless (it mutates the bar, not body/html) and acts as belt-and-
     suspenders. Folding the two into one lock authority is a later
     cleanup (see CLAUDE.md / KAN follow-up), not required for this fix.
   ===================================================================== */
(function () {
  'use strict';
  if (!('MutationObserver' in window)) return;
  // Never pin the Shopify theme-editor preview — the customizer iframe
  // can carry odd inline body styles that would falsely trip the detector.
  if (window.Shopify && window.Shopify.designMode) return;

  var html = document.documentElement;
  var body = document.body;
  if (!body) return;

  var pinned = false;   // is OUR pin currently applied?
  var savedY = 0;       // scroll offset captured at pin time
  var poll = null;      // safety poll while locked
  var selfMutating = false; // guard so our own style writes don't re-enter

  // Scrollbar-gutter compensation for desktop: when we pin body the
  // document scrollbar vanishes, which would reflow content ~15px wide.
  // Reserve that width as padding-right while pinned (no-op on mobile,
  // where overlay scrollbars have 0 width).
  function scrollbarWidth() {
    return Math.max(0, window.innerWidth - html.clientWidth);
  }

  // The lock signals overlays set on <body>. ReCharge: overflow:hidden
  // (stylesheet rule) + inline pointer-events:none. Gate on COMPUTED
  // style so it works regardless of how the lock is applied.
  function isLocked() {
    try {
      var c = getComputedStyle(body);
      return c.overflow === 'hidden' || c.pointerEvents === 'none';
    } catch (e) {
      return false;
    }
  }

  function pin() {
    if (pinned) return;
    // Coexistence guard: if something else (dialog.js) already pinned the
    // body, it owns scroll save/restore — do not stack a second pin or we
    // would capture savedY≈0 and scrollTo(0,0) the user to the top on close.
    if (body.style.position === 'fixed') return;

    savedY = window.scrollY || window.pageYOffset || 0;
    var sbw = scrollbarWidth();
    selfMutating = true;
    // Inline styles BEAT the overlay's stylesheet rules (inline > author
    // stylesheet) and override its inline position:relative, since we
    // write after it via the observer. We intentionally LEAVE any inline
    // pointer-events:none the overlay set — it disables the page behind.
    body.style.position = 'fixed';
    body.style.top = (-savedY) + 'px';
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    if (sbw > 0) body.style.paddingRight = sbw + 'px'; // desktop reflow guard
    body.classList.add('claude-scroll-pinned');
    pinned = true;
    // microtask so the observer ignores the batch of writes we just made
    Promise.resolve().then(function () { selfMutating = false; });
  }

  function unpin() {
    if (!pinned) return;
    selfMutating = true;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.overflow = '';
    body.style.paddingRight = '';
    body.classList.remove('claude-scroll-pinned');
    pinned = false;
    // Restore scroll BEFORE clearing the self-guard so our scrollTo
    // doesn't get misread as a lock change.
    window.scrollTo(0, savedY);
    Promise.resolve().then(function () { selfMutating = false; });
  }

  function sync() {
    if (selfMutating) return;
    var lockedNow = isLocked();
    if (lockedNow === pinned) return;
    if (lockedNow) {
      pin();
      // pin() may have bailed (dialog.js owns it). Only arm the poll if
      // we actually took the pin.
      if (pinned && !poll) poll = setInterval(sync, 200); // catch staggered unlocks
    } else {
      unpin();
      if (poll) { clearInterval(poll); poll = null; }
    }
  }

  var mo = new MutationObserver(sync);
  mo.observe(body, { attributes: true, attributeFilter: ['style', 'class'] });
  mo.observe(html, { attributes: true, attributeFilter: ['style', 'class'] });

  // Belt-and-suspenders: re-check shortly after any free-tee cart signal
  // (open usually follows an add) in case the lock lands without a
  // body/html attribute mutation the observer would catch.
  document.addEventListener('cftb:cart-changed', function () {
    setTimeout(sync, 60);
  });

  // Initial state (e.g. cart already open on load).
  sync();

  /* ---------------------------------------------------------------------
     STEP-2 FALLBACK (DISABLED). Only enable after a real-device repro
     shows the overlay HEIGHT (not just its top edge) is wrong. Sizes the
     locked surface to the visual viewport so an overlay that computes
     height against the layout viewport can't overshoot the visible area.
     To enable: set CLAUDE_VV_FALLBACK = true.
     --------------------------------------------------------------------- */
  var CLAUDE_VV_FALLBACK = false;
  if (CLAUDE_VV_FALLBACK && window.visualViewport) {
    var vv = window.visualViewport;
    var applyVV = function () {
      if (!pinned) return;
      body.style.height = vv.height + 'px';
    };
    vv.addEventListener('resize', applyVV);
    vv.addEventListener('scroll', applyVV);
  }
})();
